import React, { useState } from 'react';
import { Download, FileText, FileSpreadsheet, Calendar, Filter } from 'lucide-react';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';

const ExportManager = ({ user, onClose }) => {
  const [exportType, setExportType] = useState('pdf');
  const [dateRange, setDateRange] = useState('current_month');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [includeCharts, setIncludeCharts] = useState(true);
  const [includeStats, setIncludeStats] = useState(true);
  const [includeBets, setIncludeBets] = useState(true);
  const [includeExpenses, setIncludeExpenses] = useState(true);
  const [loading, setLoading] = useState(false);

  const getPeriodDates = () => {
    const now = new Date();
    let startDate, endDate;

    switch (dateRange) {
      case 'current_month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case 'last_month':
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      case 'last_3_months':
        startDate = new Date(now.getFullYear(), now.getMonth() - 2, 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case 'year':
        startDate = new Date(now.getFullYear(), 0, 1);
        endDate = new Date(now.getFullYear(), 11, 31);
        break;
      case 'custom':
        if (customStartDate && customEndDate) {
          startDate = new Date(customStartDate);
          endDate = new Date(customEndDate);
        } else {
          return null;
        }
        break;
      default:
        return null;
    }

    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    };
  };

  const fetchExportData = async () => {
    const dates = getPeriodDates();
    if (!dates && dateRange === 'custom') {
      throw new Error('Datas personalizadas são obrigatórias');
    }

    const baseParams = dates ? `&start_date=${dates.startDate}&end_date=${dates.endDate}` : '';
    
    const [statsResponse, betsResponse, expensesResponse] = await Promise.all([
      includeStats ? fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stats?user_id=${user.id}${baseParams}`) : null,
      includeBets ? fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bets?user_id=${user.id}${baseParams}`) : null,
      includeExpenses ? fetch(`${process.env.REACT_APP_BACKEND_URL}/api/expenses?user_id=${user.id}${baseParams}`) : null
    ]);

    const data = {
      stats: includeStats && statsResponse.ok ? await statsResponse.json() : null,
      bets: includeBets && betsResponse.ok ? await betsResponse.json() : [],
      expenses: includeExpenses && expensesResponse.ok ? await expensesResponse.json() : []
    };

    return data;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatPercentage = (value) => {
    return `${value.toFixed(2)}%`;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('pt-BR');
  };

  const exportToPDF = async (data) => {
    const pdf = new jsPDF();
    let yPos = 20;
    
    // Header
    pdf.setFontSize(20);
    pdf.setTextColor(0, 0, 0);
    pdf.text('Relatório Stake Mind', 20, yPos);
    yPos += 10;
    
    pdf.setFontSize(12);
    pdf.text(`Usuário: ${user.email}`, 20, yPos);
    yPos += 6;
    pdf.text(`Período: ${getDateRangeLabel()}`, 20, yPos);
    yPos += 6;
    pdf.text(`Gerado em: ${new Date().toLocaleDateString('pt-BR')}`, 20, yPos);
    yPos += 15;

    // Stats Section
    if (data.stats && includeStats) {
      pdf.setFontSize(16);
      pdf.text('📊 Estatísticas Gerais', 20, yPos);
      yPos += 10;
      
      pdf.setFontSize(11);
      const statsLines = [
        `Total de Apostas: ${data.stats.total_bets}`,
        `Apostas Green: ${data.stats.green_bets}`,
        `Taxa de Acerto: ${formatPercentage(data.stats.win_rate)}`,
        `Volume Total: ${formatCurrency(data.stats.total_volume)}`,
        `Lucro Total: ${formatCurrency(data.stats.total_profit)}`,
        `ROI: ${formatPercentage(data.stats.roi)}`
      ];
      
      statsLines.forEach(line => {
        pdf.text(line, 25, yPos);
        yPos += 6;
      });
      yPos += 10;
    }

    // Expenses Section
    if (data.expenses && data.expenses.length > 0 && includeExpenses) {
      pdf.setFontSize(16);
      pdf.text('💰 Despesas', 20, yPos);
      yPos += 10;
      
      const totalExpenses = data.expenses.reduce((sum, expense) => sum + expense.amount, 0);
      pdf.setFontSize(11);
      pdf.text(`Total de Despesas: ${formatCurrency(totalExpenses)}`, 25, yPos);
      yPos += 8;
      
      data.expenses.slice(0, 10).forEach(expense => {
        if (yPos > 270) {
          pdf.addPage();
          yPos = 20;
        }
        pdf.text(`• ${expense.description}: ${formatCurrency(expense.amount)} (${formatDate(expense.date)})`, 25, yPos);
        yPos += 6;
      });
      yPos += 10;
    }

    // Bets Section
    if (data.bets && data.bets.length > 0 && includeBets) {
      if (yPos > 250) {
        pdf.addPage();
        yPos = 20;
      }
      
      pdf.setFontSize(16);
      pdf.text('🎯 Histórico de Apostas', 20, yPos);
      yPos += 10;
      
      pdf.setFontSize(11);
      data.bets.slice(0, 15).forEach(bet => {
        if (yPos > 270) {
          pdf.addPage();
          yPos = 20;
        }
        
        const resultColor = bet.result === 'green' ? [0, 128, 0] : bet.result === 'red' ? [255, 0, 0] : [0, 0, 0];
        pdf.setTextColor(...resultColor);
        
        const betLine = `${formatDate(bet.date)} | ${bet.result.toUpperCase()} | Odd: ${bet.odd} | Stake: ${formatCurrency(bet.stake)} | Lucro: ${formatCurrency(bet.profit || 0)}`;
        pdf.text(betLine, 25, yPos);
        yPos += 6;
      });
    }

    // Footer
    pdf.setTextColor(0, 0, 0);
    pdf.setFontSize(8);
    pdf.text('Gerado por Stake Mind - Sistema de Gestão Financeira', 20, 290);

    // Save PDF
    const fileName = `stake-mind-relatorio-${dateRange}-${new Date().toISOString().split('T')[0]}.pdf`;
    pdf.save(fileName);
  };

  const exportToExcel = async (data) => {
    const workbook = XLSX.utils.book_new();
    
    // Stats Sheet
    if (data.stats && includeStats) {
      const statsData = [
        ['Métrica', 'Valor'],
        ['Total de Apostas', data.stats.total_bets],
        ['Apostas Green', data.stats.green_bets],
        ['Apostas Red', data.stats.red_bets || 0],
        ['Taxa de Acerto (%)', data.stats.win_rate],
        ['Volume Total (R$)', data.stats.total_volume],
        ['Lucro Total (R$)', data.stats.total_profit],
        ['ROI (%)', data.stats.roi]
      ];
      
      const statsSheet = XLSX.utils.aoa_to_sheet(statsData);
      XLSX.utils.book_append_sheet(workbook, statsSheet, 'Estatísticas');
    }

    // Bets Sheet
    if (data.bets && data.bets.length > 0 && includeBets) {
      const betsData = [
        ['Data', 'Resultado', 'Odd', 'Stake (R$)', 'Lucro (R$)', 'Comentários', 'Status']
      ];
      
      data.bets.forEach(bet => {
        betsData.push([
          formatDate(bet.date),
          bet.result,
          bet.odd,
          bet.stake,
          bet.profit || 0,
          bet.comments || '',
          bet.status || 'completed'
        ]);
      });
      
      const betsSheet = XLSX.utils.aoa_to_sheet(betsData);
      XLSX.utils.book_append_sheet(workbook, betsSheet, 'Apostas');
    }

    // Expenses Sheet
    if (data.expenses && data.expenses.length > 0 && includeExpenses) {
      const expensesData = [
        ['Data', 'Categoria', 'Descrição', 'Valor (R$)', 'Recorrente']
      ];
      
      data.expenses.forEach(expense => {
        expensesData.push([
          formatDate(expense.date),
          expense.category,
          expense.description,
          expense.amount,
          expense.is_recurring ? 'Sim' : 'Não'
        ]);
      });
      
      const expensesSheet = XLSX.utils.aoa_to_sheet(expensesData);
      XLSX.utils.book_append_sheet(workbook, expensesSheet, 'Despesas');
    }

    // Save Excel
    const fileName = `stake-mind-dados-${dateRange}-${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  const handleExport = async () => {
    try {
      setLoading(true);
      const data = await fetchExportData();
      
      if (exportType === 'pdf') {
        await exportToPDF(data);
      } else {
        await exportToExcel(data);
      }
      
      alert('✅ Arquivo exportado com sucesso!');
    } catch (error) {
      console.error('Export error:', error);
      alert('❌ Erro ao exportar dados. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const getDateRangeLabel = () => {
    switch (dateRange) {
      case 'current_month': return 'Mês Atual';
      case 'last_month': return 'Mês Anterior';
      case 'last_3_months': return 'Últimos 3 Meses';
      case 'year': return 'Ano Atual';
      case 'custom': return `${customStartDate} a ${customEndDate}`;
      default: return '';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
            <Download className="w-5 h-5 mr-2 text-blue-600" />
            Exportar Dados
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            ✕
          </button>
        </div>

        <div className="space-y-6">
          {/* Export Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Formato de Exportação
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setExportType('pdf')}
                className={`p-4 border rounded-lg flex flex-col items-center space-y-2 transition-colors ${
                  exportType === 'pdf'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                    : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <FileText className="w-6 h-6" />
                <span className="text-sm font-medium">PDF</span>
                <span className="text-xs text-gray-500">Relatório visual</span>
              </button>
              
              <button
                onClick={() => setExportType('excel')}
                className={`p-4 border rounded-lg flex flex-col items-center space-y-2 transition-colors ${
                  exportType === 'excel'
                    ? 'border-green-500 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                    : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <FileSpreadsheet className="w-6 h-6" />
                <span className="text-sm font-medium">Excel</span>
                <span className="text-xs text-gray-500">Planilha de dados</span>
              </button>
            </div>
          </div>

          {/* Date Range */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Período dos Dados
            </label>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="current_month">📅 Mês Atual</option>
              <option value="last_month">📆 Mês Anterior</option>
              <option value="last_3_months">📊 Últimos 3 Meses</option>
              <option value="year">🗓️ Ano Atual</option>
              <option value="custom">🎯 Período Personalizado</option>
            </select>
          </div>

          {/* Custom Date Range */}
          {dateRange === 'custom' && (
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Data Inicial
                </label>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Data Final
                </label>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>
          )}

          {/* Data Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Dados para Incluir
            </label>
            <div className="space-y-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={includeStats}
                  onChange={(e) => setIncludeStats(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-3 text-sm text-gray-700 dark:text-gray-300">📊 Estatísticas Gerais</span>
              </label>
              
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={includeBets}
                  onChange={(e) => setIncludeBets(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-3 text-sm text-gray-700 dark:text-gray-300">🎯 Histórico de Apostas</span>
              </label>
              
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={includeExpenses}
                  onChange={(e) => setIncludeExpenses(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-3 text-sm text-gray-700 dark:text-gray-300">💰 Outras Despesas</span>
              </label>
            </div>
          </div>

          {/* Export Button */}
          <div className="flex space-x-3 pt-4">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleExport}
              disabled={loading || (dateRange === 'custom' && (!customStartDate || !customEndDate))}
              className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  <span>Exportar {exportType.toUpperCase()}</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportManager;